import {useEffect, useState} from 'react';
import {ReportCategory, UserInformation} from "../types";
import {generateCodeVerifier, sha256} from "../utils";

const APIGEE_URL = (window as any).REPORT_API_URL;

const ERRORS = {
    AUTH_FAILED:"An error has occurred. Please contact your administrator.",
    INVALID_ROUTE: 'You just hit a route that doesn\'t exist... the sadness.',
    USER_INFO_FAILED: 'Failed to get user information. Please try again',
}

const AUTH_FAILED_MESSAGE = {title: 'ERROR', massage: ERRORS.AUTH_FAILED};
const newCodeVerifier = generateCodeVerifier();
export const usePKCEAuthentication = () => {
    const [token, setToken] = useState<string>('');
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [error, setError] = useState<{ title: string, massage: string } | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [code, setCode] = useState<string>('');
    const [state, setState] = useState<string>('');
    const [clientId, setClientId] = useState<string>('');
    const [userInformation, setUserInformation] = useState<UserInformation | null>(null);
    const [codeVerifier] = useState<string>(newCodeVerifier);
    const [action,setAction] = useState<ReportCategory|null>(null);

    useEffect(() => {
        const authenticate = async () => {
            const params = new URLSearchParams(window.location.search);
            const magicLink = params.get('magiclink');
            const reportAction = params.get('action') as ReportCategory;
            if (magicLink) {
                const magicLinkQuery = magicLink.split('?')[1];
                const magicLinkParams = new URLSearchParams(magicLinkQuery);
                const client_id: string = magicLinkParams.get('client_id') || '';
                const codeChallenge = await sha256(codeVerifier);
                fetch(magicLink + '&code_challenge=' + codeChallenge + '&code_challenge_method=S256').then((response) => {
                    if (response.ok) {
                        const searchQuery = response.url.split('?')[1];
                        const callbackParams = new URLSearchParams(searchQuery);
                        setClientId(client_id);
                        setAction(reportAction);
                        setCode(callbackParams.get('code') || '');
                        setState(callbackParams.get('state') || '');
                    } else {
                        applyError(AUTH_FAILED_MESSAGE);
                    }
                }, () => {
                    applyError(AUTH_FAILED_MESSAGE);
                });
            } else {
                applyError({title: 'NOT FOUND', massage: ERRORS.INVALID_ROUTE});
            }
        };
        if (codeVerifier) {
            authenticate().then();
        }
    }, [codeVerifier]);

    useEffect(() => {
        if (code && state) {
            try {
                const formData = new URLSearchParams();
                formData.append('code', code);
                formData.append('client_id', clientId);
                formData.append('grant_type', 'authorization_code');
                formData.append('state', state);
                formData.append('redirect_uri', `${window.location.origin}/callback`);
                formData.append('code_verifier', codeVerifier);
                const requestOptions = {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    }, body: formData
                } as RequestInit;

                fetch(`${APIGEE_URL}/oauth2/token`, requestOptions).then(async (response) => {
                    if (response.ok) {
                        const data = await response.json();
                        setToken(data.access_token);
                    } else {
                        applyError(AUTH_FAILED_MESSAGE);
                    }
                }, () => {
                    applyError(AUTH_FAILED_MESSAGE);
                });

            } catch {
                applyError(AUTH_FAILED_MESSAGE);
            }

        }
    }, [code, state, clientId, codeVerifier]);

    useEffect(() => {
        if (token) {
            fetch(`${APIGEE_URL}/oauth2/userinfo?scope=mc::magic_link`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                }
            }).then(async (response) => {
                if (response.ok) {
                    const info = await response.json();
                    setUserInformation(info);
                    setIsAuthenticated(true);
                    setLoading(false);

                } else {
                    applyError({title: 'Error 400:', massage: ERRORS.USER_INFO_FAILED});
                }

            }, () => {
                applyError({title: 'Error 400:', massage: ERRORS.USER_INFO_FAILED});
            });
        }
    }, [token]);

    const applyError = (error: { title: string, massage: string } | null) => {
        setError(error);
        setLoading(false);
    }
    return {isAuthenticated, token, error, loading, action , userInformation};
};
